import CustomButton from '@/components/atoms/CustomButton';
import WellnessStateCircleMobile from '@/components/molecules/WellnessStateCircle/WellnessStateCircleMobile';
import LoadingPage from '@/components/templates/LoadingPage';
import { MIN_SCREENER_SUBMISSIONS_TO_SHOW_ORG_DATA } from '@/constants';
import { FORM_FIELD_WIDTH } from '@/constants/dimensions';
import { RIGHT_ARROW_CHARACTER, SCREENER_TIME_TO_COMPLETION_MESSAGE } from '@/constants/stringVars';
import { MOBILE_DEFAULT_PADDING } from '@/constants/ui';
import usePublicScreenerOrgScoreData from '@/hooks/publicscreener/useOrgScreenerAverageScore';
import useIsWeb from '@/hooks/useIsWeb';
import { Text, VStack } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import React, { useEffect } from 'react';
import OrganizationWholenessRingExample from './OrganizationWholenessRingExample';

type Props = {
  organizationSlug: string;
  isOrganizationValid: boolean;
  showCtaButton?: boolean;
  hideExampleRing?: boolean;
};

const TryScreenerOrganizationScore = ({
  organizationSlug,
  isOrganizationValid,
  showCtaButton = true,
  hideExampleRing = false,
}: Props) => {
  const navigate = useNavigate();
  const isWeb = useIsWeb();

  const [showCollectedData, setShowCollectedData] = React.useState<boolean>(false);

  const {
    data: orgScoreData,
    isLoading,
    isSuccess,
  } = usePublicScreenerOrgScoreData({
    slug: organizationSlug ?? '',
    enabled: isOrganizationValid,
  });

  const startPublicScreener = () =>
    navigate({
      to: '/try-screener/$organizationSlug/screener',
      params: { organizationSlug: organizationSlug ?? '' },
      search: { source: 'direct' },
    });

  useEffect(() => {
    if (orgScoreData && orgScoreData?.totalSubmissions) {
      setShowCollectedData(orgScoreData.totalSubmissions >= MIN_SCREENER_SUBMISSIONS_TO_SHOW_ORG_DATA);
    }
  }, [orgScoreData, isLoading, isSuccess]);

  const showExampleRing = !showCollectedData && !hideExampleRing;

  return (
    <VStack>
      {isLoading && <LoadingPage />}
      {!isLoading && isSuccess && orgScoreData && (
        <>
          {showCollectedData && (
            <VStack marginY={'20px'} maxWidth={'100%'} overflow={'hidden'} paddingX={MOBILE_DEFAULT_PADDING}>
              <VStack marginBottom={'32px'} gap={'10px'}>
                <Text variant={'loraExtraSmallTitle'} color={'text.mediumBlue'}>
                  Your Organization’s Wholeness Ring
                </Text>
                <Text variant={'urbanistSemiBoldRegular'}>
                  {orgScoreData.totalSubmissions} co-workers have taken the screener.
                </Text>
              </VStack>

              <WellnessStateCircleMobile
                domainsData={orgScoreData?.domains}
                totalScore={orgScoreData.score ?? 0}
                showPercentageTextOnTopOfCircle
              />

              {showCtaButton && (
                <CustomButton
                  style={{ marginTop: 55, maxWidth: '360px' }}
                  label={`Start the MyOmnia Wholeness Screener ${RIGHT_ARROW_CHARACTER}`}
                  backgroundColor="primary.500"
                  labelColor="extra.white"
                  onClick={startPublicScreener}
                  width={'100%'}
                  height={54}
                />
              )}
              {showCtaButton && (
                <Text variant={'urbanistSemiBoldRegular'} color={'text.mediumGray'} mt={'20px'}>
                  {SCREENER_TIME_TO_COMPLETION_MESSAGE}
                </Text>
              )}
            </VStack>
          )}
          {showExampleRing && (
            <VStack marginY={'20px'} gap={'40px'} width={'100%'} maxWidth={'100%'} overflow={'hidden'}>
              <VStack paddingX={MOBILE_DEFAULT_PADDING} gap={0}>
                {orgScoreData?.totalSubmissions === 0 && (
                  <>
                    <Text variant={'loraExtraSmallTitle'} color={'text.mediumBlue'} mb={'10px'}>
                      No Screeners yet, be the first!
                    </Text>
                    <Text variant={'urbanistSemiBoldRegular'} mb={'20px'}>
                      Don’t worry, your results will remain confidential.
                    </Text>
                  </>
                )}
                {showCtaButton && (
                  <CustomButton
                    label={`Start the MyOmnia Wholeness Screener ${RIGHT_ARROW_CHARACTER}`}
                    backgroundColor="secondary.500"
                    labelColor="extra.white"
                    onClick={startPublicScreener}
                    width={isWeb ? FORM_FIELD_WIDTH : '100%'}
                  />
                )}
                {showCtaButton && (
                  <Text variant={'urbanistSemiBoldRegular'} color={'text.mediumGray'} mt={'14px'}>
                    {SCREENER_TIME_TO_COMPLETION_MESSAGE}
                  </Text>
                )}
              </VStack>
              <OrganizationWholenessRingExample
                atLeastCounter={MIN_SCREENER_SUBMISSIONS_TO_SHOW_ORG_DATA - (orgScoreData?.totalSubmissions ?? 0)}
              />
            </VStack>
          )}
        </>
      )}
    </VStack>
  );
};

export default TryScreenerOrganizationScore;
